import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

// ANIMATIONS

// COMPONENTS

// CONFIG

// INTERFACES
import { IStock } from '../../../rfq/interfaces/rfq-stock.interface';
import {
  slideDownAnimation,
  slideUpAnimation,
  rollDownAnimation,
} from '../../../shared/animations/animation';
import { DialogConfig } from '../../../shared/configs/dialog.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// SERVICES
import { RfqStockLocationApiService } from '../../../shared/services/api/rfq/rfq-stock-location-api.service';

// UTILS
import { trackByValue } from '../../../shared/util/track-by.util';
import { UiConfirmationDialogComponent } from '../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

@Component({
  selector: 'lum-location-warehouse-quantity-entry',
  templateUrl: './location-warehouse-quantity-entry.component.html',
  styleUrls: ['./location-warehouse-quantity-entry.component.scss'],
  animations: [slideDownAnimation, slideUpAnimation, rollDownAnimation],
})
export class LocationWarehouseQuantityEntryComponent implements OnInit, OnDestroy {
  stock :IStock;
  loading :boolean = false;
  relations :string = 'location,rfqStock,rfqStock.rfq';
  toggleCalculator :boolean = false;
  savedResults :number[] = [];
  value :number = 0;
  quantityControl = new UntypedFormControl(0);

  trackByValue = trackByValue;

  private onDestroy$ = new Subject();

  constructor(
    private route :ActivatedRoute,
    private router :Router,
    private rfqStockLocationApiService :RfqStockLocationApiService,
    private cd :ChangeDetectorRef,
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.getRouterQueryParams().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onValueChange(value :number) :void {
    this.value = value;
  }

  onUpdate() :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Action',
          message: `Set  ${this.stock?.rfqStock?.rfq?.internalSku} quantity to ${this.quantityControl.value}?`,
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.rfqStockLocationApiService
            .updateQuantity({
              ...this.stock,
              onHandQuantity: this.quantityControl.value,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.snackBar.open(
                  `Quantity updated to ${response.onHandQuantity}`,
                  'Success',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );
                this.router.navigate(['/warehouse-mobile-app/devices']);
              },
              error: (error) => {
                this.snackBar.open(`Failed to update quantity`, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                return error;
              },
            });
        }
      });
  }

  onSaveResults() :void {
    if (this.value) {
      this.savedResults.push(this.value);
      this.quantityControl.setValue(this.totalSavedResults);
      this.value = 0;
    }
  }

  onRemoveSavedResult(index :number) :void {
    const totalSavedResults = this.totalSavedResults;
    this.savedResults.splice(index, 1);
    if (this.quantityControl.value === totalSavedResults) {
      this.quantityControl.setValue(this.totalSavedResults);
    }
  }

  private getRouterQueryParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const id = params['rfq_stock_location_id'];
        this.loading = true;
        if (id) {
          this.getRfqStock(id);
        }
      }),

      takeUntil(this.onDestroy$),
    );
  }

  private getRfqStock(id :number) :void {
    this.rfqStockLocationApiService
      .getById({
        id,
        params: { relations: this.relations },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.stock = response;
          this.loading = false;
          this.cd.detectChanges();
        },
        error: (error) => {
          this.loading = false;

          return error;
        },
      });
  }

  get totalSavedResults() :number {
    return this.savedResults.reduce((acc, cur) => acc + cur, 0);
  }
}
